/**
 * CamerasTopologyCanvas — quién graba a quién.
 *
 * Mismo espíritu que la topología WiFi: una vista de un vistazo. En vez de un
 * grafo libre (que hay que arrastrar y recolocar), se pinta un **árbol por
 * capas**: grabadores arriba (con su IP, rack y canales) y sus cámaras debajo,
 * unidas por una línea. Las cámaras sin grabador van en su propio bloque, que
 * es justo lo que hay que ver para detectar el problema.
 *
 * Se puede buscar y filtrar por estado; los contadores de la cabecera resumen
 * grabadores, cámaras, sin grabador y cámaras caídas.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  VideoCameraIcon,
  ServerStackIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import './CamerasTopology.css';

export default function CamerasTopologyCanvas({ companyId }) {
  const [data, setData] = useState({ recorders: [], cameras: [], unassigned: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [soloCaidas, setSoloCaidas] = useState(false);

  const cargar = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.getCamerasTopology(companyId);
      setData({
        recorders: res?.recorders || [],
        cameras: res?.cameras || [],
        unassigned: res?.unassigned || [],
      });
    } catch (err) {
      setError(err.message || 'No se pudo cargar la topología');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { cargar(); }, [cargar]);

  const coincide = useCallback((elemento) => {
    const q = search.trim().toLowerCase();
    const texto = !q
      || (elemento.name || '').toLowerCase().includes(q)
      || (elemento.ip_address || '').toLowerCase().includes(q)
      || (elemento.model || '').toLowerCase().includes(q);
    const estado = !soloCaidas || elemento.status !== 'active';
    return texto && estado;
  }, [search, soloCaidas]);

  const camerasByRecorder = useMemo(() => {
    const map = new Map();
    (data.cameras || []).forEach((camera) => {
      if (!camera.recorder_id) return;
      const list = map.get(camera.recorder_id) || [];
      list.push(camera);
      map.set(camera.recorder_id, list);
    });
    return map;
  }, [data.cameras]);

  const caidas = useMemo(
    () => (data.cameras || []).filter((camera) => camera.status !== 'active'),
    [data.cameras],
  );

  const grabadoresVisibles = (data.recorders || []).filter((recorder) => {
    const camaras = (camerasByRecorder.get(recorder.id) || []).filter(coincide);
    return coincide(recorder) || camaras.length > 0;
  });

  const sinGrabador = (data.unassigned || []).filter(coincide);

  const resumen = [
    { label: 'Grabadores', value: (data.recorders || []).length, tone: 'accent' },
    { label: 'Cámaras', value: (data.cameras || []).length, tone: 'on' },
    { label: 'Sin grabador', value: (data.unassigned || []).length, tone: (data.unassigned || []).length ? 'warn' : 'muted' },
    { label: 'No activas', value: caidas.length, tone: caidas.length ? 'off' : 'muted' },
  ];

  return (
    <div className="ct-page">
      <div className="list-head">
        <div className="list-head-left">
          <VideoCameraIcon className="list-head-icon" />
          <div>
            <h1 className="list-title">Topología de cámaras</h1>
            <p className="list-subtitle">Qué cámara graba en qué grabador, y qué se ha quedado suelto.</p>
          </div>
        </div>
        <div className="list-head-actions">
          <div className="list-head-search">
            <input
              type="search"
              className="list-search list-search--inline"
              placeholder="Buscar cámara, IP o modelo…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <button
            type="button"
            className="list-btn-secondary"
            onClick={() => setSoloCaidas((prev) => !prev)}
            aria-pressed={soloCaidas}
            style={soloCaidas ? { borderColor: 'rgba(var(--accent-rgb), 0.5)', color: 'var(--accent-l)' } : undefined}
          >
            {soloCaidas ? '✓ ' : ''}Solo no activas
          </button>
          <button type="button" className="list-btn-secondary" onClick={cargar} title="Recargar">
            <ArrowPathIcon style={{ width: 15, height: 15 }} />
          </button>
        </div>
      </div>

      <div className="ct-kpis">
        {resumen.map((kpi) => (
          <div key={kpi.label} className={`ct-kpi ct-kpi--${kpi.tone}`}>
            <span className="ct-kpi-value">{kpi.value}</span>
            <span className="ct-kpi-label">{kpi.label}</span>
          </div>
        ))}
      </div>

      {loading && <p className="list-hint">Cargando la topología…</p>}
      {error && <p style={{ color: 'var(--danger, #ef4444)' }}>{error}</p>}

      {!loading && !error && grabadoresVisibles.length === 0 && sinGrabador.length === 0 && (
        <div className="ct-empty">
          <MagnifyingGlassIcon style={{ width: 30, height: 30, opacity: 0.6 }} />
          <p>No hay grabadores ni cámaras que coincidan.</p>
          <Link className="list-btn-primary" to="/cameras/recorders">Dar de alta un grabador</Link>
        </div>
      )}

      <div className="ct-forest">
        {grabadoresVisibles.map((recorder) => {
          const camaras = (camerasByRecorder.get(recorder.id) || []).filter(coincide);
          return (
            <section key={recorder.id} className="ct-branch">
              <div className="ct-branch-head">
                <div className="ct-node ct-node--recorder">
                  <ServerStackIcon style={{ width: 18, height: 18 }} />
                  <div>
                    <Link to={`/cameras/recorders?destacar=${recorder.id}&abrir=1`} className="ct-node-title">
                      {recorder.name}
                    </Link>
                    <div className="ct-node-meta">
                      {recorder.ip_address || 'sin IP'}
                      {recorder.rack_name ? ` · ${recorder.rack_name}` : ''}
                      {recorder.channels ? ` · ${recorder.channels} canales` : ''}
                    </div>
                  </div>
                  <span className={`list-badge ${recorder.status === 'active' ? 'list-badge--on' : 'list-badge--off'}`}>
                    {recorder.status_label || recorder.status}
                  </span>
                </div>
                {recorder.parent_name && (
                  <span className="ct-parent">cuelga de {recorder.parent_name}</span>
                )}
              </div>

              <div className="ct-cameras">
                {camaras.length === 0 && (
                  <span className="ct-no-cameras">
                    <ExclamationTriangleIcon style={{ width: 13, height: 13 }} /> sin cámaras
                  </span>
                )}
                {camaras.map((camera) => (
                  <Link
                    key={camera.id}
                    to={`/cameras/list?destacar=${camera.id}&abrir=1`}
                    className={`ct-node ct-node--camera${camera.status !== 'active' ? ' ct-node--alert' : ''}`}
                    title={`${camera.name}${camera.ip_address ? ` · ${camera.ip_address}` : ''}`}
                  >
                    <VideoCameraIcon style={{ width: 14, height: 14 }} />
                    <span className="ct-camera-name">{camera.name}</span>
                    {camera.channel ? <span className="ct-camera-channel">CH {camera.channel}</span> : null}
                    {camera.ip_address && <span className="ct-camera-ip">{camera.ip_address}</span>}
                  </Link>
                ))}
              </div>
            </section>
          );
        })}

        {sinGrabador.length > 0 && (
          <section className="ct-branch ct-branch--orphan">
            <div className="ct-branch-head">
              <div className="ct-node ct-node--recorder ct-node--orphan">
                <ExclamationTriangleIcon style={{ width: 18, height: 18 }} />
                <div>
                  <span className="ct-node-title">Sin grabador asignado</span>
                  <div className="ct-node-meta">Estas cámaras no están grabando en ningún NVR/DVR</div>
                </div>
                <span className="list-badge list-badge--warn">{sinGrabador.length}</span>
              </div>
            </div>
            <div className="ct-cameras">
              {sinGrabador.map((camera) => (
                <Link
                  key={camera.id}
                  to={`/cameras/list?destacar=${camera.id}&abrir=1`}
                  className="ct-node ct-node--camera ct-node--orphan"
                >
                  <VideoCameraIcon style={{ width: 14, height: 14 }} />
                  <span className="ct-camera-name">{camera.name}</span>
                  {camera.ip_address && <span className="ct-camera-ip">{camera.ip_address}</span>}
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
